import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { LngLat, Point } from 'mapbox-gl';

export class MapTestService {
    map: MapboxMap | null;
    mapCanvasLeft: number;
    mapCanvasTop: number;
    mapCanvasWidth: number;
    mapCanvasHeight: number;

    private _allLayerNames: string[] = [];
    private _dealerLayerNames: string[] = [];
    private _eventsLayerNames: string[] = [];
    private _geofenceLayerNames: string[] = [];
    private _groupRideLayerNames: string[] = [];
    private _miscLayerNames: string[] = [];
    private _noGoZoneLayerNames: string[] = [];
    private _outfitterLayerNames: string[] = [];
    private _parcelLayerNames: string[] = [];
    private _plannedRideLayerNames: string[] = [];
    private _poiLayerNames: string[] = [];
    private _rideAreaLayerNames: string[] = [];
    private _sponsorLayerNames: string[] = [];
    private _trackedRideHeatMapLayerNames: string[] = [];
    private _trackedRidesLayerNames: string[] = [];
    private _trailClosedLayerNames: string[] = [];
    private _trailDifficultyLayerNames: string[] = [];
    private _trailLayerNames: string[] = [];
    private _waypointLayerNames: string[] = [];

    constructor(map: MapboxMap) {
        this.map = map;
        this.getMapCanvas();
    }

    //#region Layer Names
    get allLayerNames() {
        if (this._allLayerNames.length === 0) {
            this._allLayerNames = this.map
                ? this.map.getStyle().layers.map((name) => name.id)
                : [];
        }

        return this._allLayerNames;
    }

    /*
        "dealersUnselected",
        "dealersSelected",
    */
    get dealerLayerNames() {
        if (this._dealerLayerNames.length === 0) {
            this._dealerLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('dealers')
            );
        }

        return this._dealerLayerNames;
    }

    /*
        "eventsUnselected",
        "eventsSelected",
    */
    get eventsLayerNames() {
        if (this._eventsLayerNames.length === 0) {
            this._eventsLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('events')
            );
        }

        return this._eventsLayerNames;
    }

    /*
        "geofencesUnselected",
        "geofencesUnselectedBorder2",
        "geofencesUnselectedBorder1",
        "geofencesUnselectedLabel",
        "geofencesEditingPolygon",
        "geofencesEditingDrawnLineOutline",
        "geofencesEditingUndrawnOutline",
        "geofencesEditingDrawnLine",
        "geofencesEditingUndrawn",
        "geofencesEditingPointsOutline",
        "geofencesEditingPoints",
        "geofencesSelected",
        "geofencesSelectedBorder3",
        "geofencesSelectedBorder2",
        "geofencesSelectedBorder1",
    */
    get geofenceLayerNames() {
        if (this._geofenceLayerNames.length === 0) {
            this._geofenceLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('geofences')
            );
        }

        return this._geofenceLayerNames;
    }

    /*
        "groupRideCheckInUnselectedPath1",
        "groupRideCheckInUnselectedPath2",
        "groupRideCheckInUnselectedPath3",
        "groupRideUnselectedHelpAlertAnimation",
        "groupRideUnselectedLeaderLine",
        "groupRideUnselectedBearing",
        "groupRideUnselectedIconRingShadow",
        "groupRideUnselectedIconGroupride",
        "groupRideCheckInUnselectedIcon",
        "groupRideSelectedHelpAlertAnimation",
        "groupRideSelectedIconCircle",
        "groupRideSelectedIconRingShadow",
        "groupRideSelectedBearing_copy",
        "groupRideSelectedBearingOutline",
        "groupRideSelectedIconGroupRide",
        "groupRideSelectedonRing",
        "groupRideUnselectedLocationAlertPath1",
        "groupRideUnselectedLocationAlertPath",
        "groupRideSelectedLocationAlertPath2",
        "groupRideSelectedLocationAlertPath1",
        "groupRideSelectedLocationAlertPath",
        "groupRideUnselectedIconConnectedShadow ",
        "groupRideUnselectedActiveAlertAnimation",
        "groupRideUnselectedAlertsEnabledAnimation",
        "groupRideUnselectedIconConnected",
        "groupRideUnselectedIconConnectedBackground",
        "groupRideUnselectedIconConnectedGroupride",
        "groupRideUnselectedAddressLabel",
        "groupRideCheckInSelectedIcon",
        "groupRideSelectedIconConnectedShadow",
        "groupRideSelectedConnectedGrouprideActiveAlertAnimation",
        "groupRideSelectedConnectedGrouprideActiveAlert",
        "groupRideSelectedConnectedGrouprideAlertsEnabledAnimation",
        "groupRideSelectedConnectedGrouprideAlertsEnabled",
        "groupRideSelectedIconConnectedHalo",
        "groupRideSelectedIconConnected",
        "groupRideSelectedIconConnectedBackground",
        "groupRideSelectedConnectedGroupride",
        "groupRideSelectedAddressLabel",
        "groupRideSelectedWhiteOutline",
        "groupRideSelectedHelpAlert",
        "groupRideUnselectedHelpAlert",
    */
    get groupRideLayerNames() {
        if (this._groupRideLayerNames.length === 0) {
            this._groupRideLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('groupRide')
            );
        }

        return this._groupRideLayerNames;
    }

    /*
        "tempPlace",
        "tempWaypoint",
        "userLocationArrow",
    */
    get miscLayerNames() {
        if (this._miscLayerNames.length === 0) {
            this._miscLayerNames = this.allLayerNames.filter((name) =>
                ['tempPlace', 'tempWaypoint', 'userLocationArrow'].includes(
                    name
                )
            );
        }

        return this._miscLayerNames;
    }

    /*
        "noGoZonesUnselected",
        "noGoZonesSelected",
        "noGoZonesSelectedOutline",
        "noGoZonesSelectedOutline2",
        "Nogozones-label",
    */
    get noGoZoneLayerNames() {
        if (this._noGoZoneLayerNames.length === 0) {
            this._noGoZoneLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('noGoZones')
            );
        }

        return this._noGoZoneLayerNames;
    }

    /*
        "outfittersUnselected",
        "outfittersSelected",
    */
    get outfitterLayerNames() {
        if (this._outfitterLayerNames.length === 0) {
            this._outfitterLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('outfitters')
            );
        }

        return this._outfitterLayerNames;
    }

    /*
        "PrivateLandUnselectedSouthEastArea",
        "PrivateLandUnselectedSouthEast",
        "PrivateLandUnselectedNorthEastArea",
        "PrivateLandUnselectedNorthEast",
        "PrivateLandUnselectedWestArea",
        "PrivateLandUnselectedWest",
        "PrivateLandSelectedSouthEast",
        "PrivateLandSelectedSouthEast3",
        "PrivateLandSelectedSouthEast2",
        "PrivateLandSelectedSouthEast1",
        "PrivateLandSelectedNorthEast",
        "PrivateLandSelectedNorthEast3",
        "PrivateLandSelectedNorthEast2",
        "PrivateLandSelectedNorthEast1",
        "PrivateLandSelectedWest",
        "PrivateLandSelectedWest3",
        "PrivateLandSelectedWest2",
        "PrivateLandSelectedWest1",
        "PrivateLandUnselected-Label SouthEast",
        "PrivateLandSelected-Label SouthEast",
        "PrivateLandUnselected-Label NorthEast",
        "PrivateLandSelected-Label NorthEast",
        "PrivateLandUnselected-Label West",
        "PrivateLandSelected-Label West",
        "MixedLandUnselectedSouthEastArea",
        "MixedLandUnselectedSouthEast",
        "MixedLandUnselectedNorthEastArea",
        "MixedLandUnselectedNorthEast",
        "MixedLandUnselectedWestArea",
        "MixedLandUnselectedWest",
        "MixedLandSelectedSouthEast",
        "MixedLandSelectedStrokeSouthEast",
        "MixedLandSelectedWhiteStrokeSouthEast",
        "MixedLandSelectedOuterStrokeSouthEast",
        "MixedLandSelectedNorthEast",
        "MixedLandSelectedStrokeNorthEast",
        "MixedLandSelectedWhiteStrokeNorthEast",
        "MixedLandSelectedOuterStrokeNorthEast",
        "MixedLandSelectedWest",
        "MixedLandSelectedStrokeWest",
        "MixedLandSelectedWhiteStrokeWest",
        "MixedLandSelectedOuterStrokeWest",
        "MixedLandUnselected-Label SouthEast",
        "MixedLandSelected-Label SouthEast",
        "MixedLandUnselected-Label West Parks",
        "MixedLandUnselected-Label West",
        "MixedLandSelected-Label West",
        "MixedLandUnselected-Label NorthEast",
        "MixedLandSelected-Label NorthEast",
    */
    get parcelLayerNames() {
        if (this._parcelLayerNames.length === 0) {
            this._parcelLayerNames = this.allLayerNames.filter(
                (name) =>
                    name.startsWith('PrivateLand') ||
                    name.startsWith('MixedLand')
            );
        }

        return this._parcelLayerNames;
    }

    /*
        "plannedRidesOffTrailUnselectedPath1",
        "plannedRidesOffTrailUnselectedPath2",
        "plannedRidesUnselectedPath2",
        "plannedRidesUnselectedPath1",
        "plannedRidesUnselectedDot",
        "plannedRidesUnselected",
        "plannedRidesOffTrailEditPath3",
        "plannedRidesOffTrailSelectedPath3",
        "plannedRidesOffTrailEditPath3 copy",
        "plannedRidesOffTrailSelectedPath3 copy",
        "plannedRidesOffTrailEditPath2 copy",
        "plannedRidesOffTrailSelectedPath2 copy",
        "plannedRidesOffTrailEditPath1",
        "plannedRidesOffTrailSelectedPath1",
        "plannedRidesOffTrailEditLabel",
        "plannedRidesOffTrailSelectedLabel",
        "plannedRidesEditPath3",
        "plannedRidesSelectedPath3",
        "plannedRidesEditPath2",
        "plannedRidesSelectedPath2",
        "plannedRidesEditPath1",
        "plannedRidesSelectedPath1",
        "plannedRidesSelectedStopDot",
        "plannedRidesEditStopsNoColor",
        "plannedRidesSelectedStopsNoColor",
        "plannedRidesEditStopsColor",
        "plannedRidesSelectedStopsColor",
        "plannedRidesEditTempWaypoint",
        "plannedRidesEditViapoint2",
        "plannedRidesEditViapoint1",
        "plannedRidesEditTempViapoint2",
        "plannedRidesEditTempViapoint1",
    */
    get plannedRideLayerNames() {
        if (this._plannedRideLayerNames.length === 0) {
            this._plannedRideLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('plannedRides')
            );
        }

        return this._plannedRideLayerNames;
    }

    /*
        "poisUnselectedDot",
        "poisUnselected",
        "poisSelected",
    */
    get poiLayerNames() {
        if (this._poiLayerNames.length === 0) {
            this._poiLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('pois')
            );
        }

        return this._poiLayerNames;
    }

    /*
        "OHVRideAreascopyUnselected",
        "OHVRideAreasOutlinecopy3Unselected",
        "OHVRideAreasOutlinecopy2Unselected",
        "OHVRideAreasSelected",
        "OHVRideAreasOutlinecopySelected",
        "OHVRideAreasOutlinecopy1Selected",
        "OHVRideAreasOutlineSelected",
        "OHVRideAreasLabelEdge",
        "OHVRideAreasLabelCenter",
        "rideAreasUnselected",
        "rideAreasSelected",
    */
    get rideAreaLayerNames() {
        if (this._rideAreaLayerNames.length === 0) {
            this._rideAreaLayerNames = this.allLayerNames.filter(
                (name) =>
                    name.startsWith('OHVRideAreas') ||
                    name.startsWith('rideAreas')
            );
        }

        return this._rideAreaLayerNames;
    }

    /*
        "sponsorsUnselectedDot",
        "sponsorsUnselected",
        "sponsorsSelected",
    */
    get sponsorLayerNames() {
        if (this._sponsorLayerNames.length === 0) {
            this._sponsorLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('sponsors')
            );
        }

        return this._sponsorLayerNames;
    }

    /*
        "trackedRideHeatMap",
        "trackedRideHeatMap1",
        "trackedRideHeatMap2",
        "trackedRideHeatMap3",
        "trackedRideHeatMap4",
    */
    get trackedRideHeatMapLayerNames() {
        if (this._trackedRideHeatMapLayerNames.length === 0) {
            this._trackedRideHeatMapLayerNames = this.allLayerNames.filter(
                (name) => name.startsWith('trackedRideHeatMap')
            );
        }

        return this._trackedRideHeatMapLayerNames;
    }

    /*
        "connectedTrackedRidesActiveUnselectedPath2",
        "connectedTrackedRidesActiveUnselectedPath1",
        "connectedTrackedRidesActiveSelectedPath3",
        "connectedTrackedRidesActiveSelectedPath2",
        "connectedTrackedRidesActiveSelectedPath1",
        "trackedRidesUnselectedStart",
        "trackedRidesSelectedPath3",
        "trackedRidesSelectedPath2",
        "trackedRidesSelectedPath1",
        "trackedRidesSelectedStart",
        "trackedRidesActivePath3",
        "trackedRidesActivePath2",
        "trackedRidesActivePath1",
        "trackedRidesActiveStart",
        "trackedRidesUnselectedPath2",
        "trackedRidesUnselectedPath1",
        "trackedRidesUnselectedStartDot",
        "trackedRidesName",
        "trackedRidesSelectedName",
    */
    get trackedRidesLayerNames() {
        if (this._trackedRidesLayerNames.length === 0) {
            this._trackedRidesLayerNames = this.allLayerNames.filter(
                (name) =>
                    name.startsWith('connectedTrackedRides') ||
                    name.startsWith('trackedRides')
            );
        }

        return this._trackedRidesLayerNames;
    }

    /*
        "trailClosedOHMATVROV50",
        "trailClosedROVANY",
    */
    get trailClosedLayerNames() {
        if (this._trailClosedLayerNames.length === 0) {
            this._trailClosedLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('TrailClosed')
            );
        }

        return this._trailClosedLayerNames;
    }

    /*
        "DifficultyLine",
        "Difficulty",
    */
    get trailDifficultyLayerNames() {
        if (this._trailDifficultyLayerNames.length === 0) {
            this._trailDifficultyLayerNames = this.allLayerNames.filter(
                (name) => name.startsWith('Difficulty')
            );
        }

        return this._trailDifficultyLayerNames;
    }

    /*
        "trailsOhmLine2",
        "trailsOhmLine1",
        "trailsUnmaintainedLine2",
        "trailsUnmaintainedLine1",
        "trailsNon-DesignatedUnpavedLine",
        "trailsNon-DesignatedPavedLine",
        "trailsAtvLine2",
        "trailsAtvLine1",
        "trailsRov50Line2",
        "trailsRov50Line1",
        "trailsRovLine2",
        "trailsRovLine1",
        "trailsAllLine2",
        "trailsAllLine1",
        "trailsOhmLabel",
        "trailsUnmaintainedLabel",
        "trailsNon-DesignatedUnpavedLabel",
        "trailsNon-DesignatedPavedLabel",
        "trailsAtvLabel",
        "trailsRov50Label",
        "trailsRovLabel",
        "trailsAllLabel",
    */
    get trailLayerNames() {
        if (this._trailLayerNames.length === 0) {
            this._trailLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('trails')
            );
        }

        return this._trailLayerNames;
    }

    /*
        "waypointsPublicUnselectedDot",
        "waypointsUnselectedDot",
        "waypointsUnselectedDotWeb",
        "waypointsPublicUnselected",
        "waypointsUnselected",
        "waypointsPublicSelected",
        "waypointsSelected",
        "waypointsSelectedWeb",
    */
    get waypointLayerNames() {
        if (this._waypointLayerNames.length === 0) {
            this._waypointLayerNames = this.allLayerNames.filter((name) =>
                name.startsWith('')
            );
        }

        return this._waypointLayerNames;
    }
    //#endregion

    //#region Map
    destroy() {
        this.map = null;
        this.mapCanvasHeight = 0;
        this.mapCanvasWidth = 0;
        this.mapCanvasLeft = 0;
        this.mapCanvasTop = 0;
    }

    zoomTo(lng: number, lat: number, zoom = 15) {
        if (!this.map) {
            return;
        }

        this.map.jumpTo({ center: { lat, lng }, zoom });
    }

    getMapCenterLngLat() {
        if (!this.map) {
            return new LngLat(0, 0);
        }

        return this.map.getCenter();
    }

    getLngLatAtMapXY(x: number, y: number) {
        if (!this.map) {
            return new LngLat(0, 0);
        }

        return this.map.unproject([x, y]);
    }

    getLngLatAtWindowXY(x: number, y: number) {
        return this.getLngLatAtMapXY(
            x - this.mapCanvasLeft,
            y - this.mapCanvasTop
        );
    }

    getMapXYAtLngLat(lng: number, lat: number) {
        if (!this.map) {
            return new Point(0, 0);
        }
        return this.map.project([lng, lat]);
    }
    //#endregion

    isDealerAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.dealerLayerNames);
    }

    isEventAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.eventsLayerNames);
    }

    isGeofenceAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.geofenceLayerNames);
    }

    isNoGoZoneAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.noGoZoneLayerNames);
    }

    isOutfitterAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.outfitterLayerNames);
    }

    isParcelAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.parcelLayerNames);
    }

    isPlannedRideAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.plannedRideLayerNames);
    }

    isPoiAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.poiLayerNames);
    }

    isRideAreaAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.rideAreaLayerNames);
    }

    isSponsorAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.sponsorLayerNames);
    }

    isTrackedRideHeatMapAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.trackedRideHeatMapLayerNames);
    }

    isTrackedRideAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.trackedRidesLayerNames);
    }

    isTrailClosedAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.trailClosedLayerNames);
    }

    isTrailDifficultyAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.trailDifficultyLayerNames);
    }

    isTrailAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.trailLayerNames);
    }

    isWaypointAtCoordinate(lng: number, lat: number) {
        return this.isItemAtPoint(lng, lat, this.waypointLayerNames);
    }

    getDealerAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.dealerLayerNames);
    }

    getEventAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.eventsLayerNames);
    }

    getGeofenceAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.geofenceLayerNames);
    }

    getNoGoZoneAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.noGoZoneLayerNames);
    }

    getOutfitterAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.outfitterLayerNames);
    }

    getParcelAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.parcelLayerNames);
    }

    getPlannedRideAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.plannedRideLayerNames);
    }

    getPoiAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.poiLayerNames);
    }

    getRideAreaAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.rideAreaLayerNames);
    }

    getSponsorAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.sponsorLayerNames);
    }

    getTrackedRideHeatMapAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.trackedRideHeatMapLayerNames);
    }

    getTrackedRideAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.trackedRidesLayerNames);
    }

    getTrailClosedAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.trailClosedLayerNames);
    }

    getTrailDifficultyAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.trailDifficultyLayerNames);
    }

    getTrailAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.trailLayerNames);
    }

    getWaypointAtCoordinate(lng: number, lat: number) {
        return this.getItemAtPoint(lng, lat, this.waypointLayerNames);
    }

    getDealersInView() {
        return this.getItemsInView(this.dealerLayerNames);
    }

    getEventsInView() {
        return this.getItemsInView(this.eventsLayerNames);
    }

    getGeofencesInView() {
        return this.getItemsInView(this.geofenceLayerNames);
    }

    getNoGoZonesInView() {
        return this.getItemsInView(this.noGoZoneLayerNames);
    }

    getOutfittersInView() {
        return this.getItemsInView(this.outfitterLayerNames);
    }

    getParcelsInView() {
        return this.getItemsInView(this.parcelLayerNames);
    }

    getPlannedRidesInView() {
        return this.getItemsInView(this.plannedRideLayerNames);
    }

    getPoisInView() {
        return this.getItemsInView(this.poiLayerNames);
    }

    getRideAreasInView() {
        return this.getItemsInView(this.rideAreaLayerNames);
    }

    getSponsorsInView() {
        return this.getItemsInView(this.sponsorLayerNames);
    }

    getTrackedRideHeatMapsInView() {
        return this.getItemsInView(this.trackedRideHeatMapLayerNames);
    }

    getTrackedRidesInView() {
        return this.getItemsInView(this.trackedRidesLayerNames);
    }

    getTrailClosedInView() {
        return this.getItemsInView(this.trailClosedLayerNames);
    }

    getTrailDifficultyInView() {
        return this.getItemsInView(this.trailDifficultyLayerNames);
    }

    getTrailsInView() {
        return this.getItemsInView(this.trailLayerNames);
    }

    getWaypointsInView() {
        return this.getItemsInView(this.waypointLayerNames);
    }

    isAnyDealerInView() {
        return Boolean(this.getDealersInView());
    }

    isAnyEventInView() {
        return Boolean(this.getEventsInView());
    }

    isAnyGeofenceInView() {
        return Boolean(this.getGeofencesInView());
    }

    isAnyNoGoZoneInView() {
        return Boolean(this.getNoGoZonesInView());
    }

    isAnyOutfitterInView() {
        return Boolean(this.getOutfittersInView());
    }

    isAnyParcelInView() {
        return Boolean(this.getParcelsInView());
    }

    isAnyPlannedRideInView() {
        return Boolean(this.getPlannedRidesInView());
    }

    isAnyPoiInView() {
        return Boolean(this.getPoisInView());
    }

    isAnyRideAreaInView() {
        return Boolean(this.getRideAreasInView());
    }

    isAnySponsorInView() {
        return Boolean(this.getSponsorsInView());
    }

    isAnyTrackedRideHeatMapInView() {
        return Boolean(this.getTrackedRideHeatMapsInView());
    }

    isAnyTrackedRideInView() {
        return Boolean(this.getTrackedRidesInView());
    }

    isAnyTrailClosedInView() {
        return Boolean(this.getTrailClosedInView());
    }

    isAnyTrailDifficultyInView() {
        return Boolean(this.getTrailDifficultyInView());
    }

    isAnyTrailInView() {
        return Boolean(this.getTrailsInView());
    }

    isAnyWaypointInView() {
        return Boolean(this.getWaypointsInView());
    }

    private isItemAtPoint(lng: number, lat: number, layerNames: string[]) {
        return Boolean(this.getItemAtPoint(lng, lat, layerNames));
    }

    private getItemAtPoint(lng: number, lat: number, layerNames: string[]) {
        if (!this.map) {
            return null;
        }

        const itemXY = this.getMapXYAtLngLat(lng, lat);
        const matches = this.map.queryRenderedFeatures(
            [
                [itemXY.x - 5, itemXY.y - 5],
                [itemXY.x + 5, itemXY.y + 5],
            ],
            {
                layers: layerNames,
            }
        );

        return matches[0];
    }

    private getItemsInView(layerNames: string[]) {
        if (!this.map) {
            return [];
        }

        return this.map.queryRenderedFeatures(
            [
                [0, 0],
                [this.mapCanvasWidth, this.mapCanvasHeight],
            ],
            { layers: layerNames }
        );
    }

    private getMapCanvas() {
        if (!this.map) {
            return;
        }

        const mapCanvas = this.map.getCanvas();
        this.mapCanvasHeight = mapCanvas.clientHeight;
        this.mapCanvasWidth = mapCanvas.clientWidth;
        this.mapCanvasLeft = mapCanvas.clientLeft;
        this.mapCanvasTop = mapCanvas.clientTop;
    }
}
